import type { RuleGroupType, RuleType } from "react-querybuilder";
import {
  buildConditionalPlansQuery,
  fetchConditionalPlans,
  normalizePlanTypeApiCode,
  type ConditionalPlanRecord,
} from "../../../services/autoTaggingService";
import { parseEnhancedTaggingConditionValue } from "./enhancedTaggingConditionValue";
import type { EnhancedTaggingThenTag } from "./EnhancedTaggingRuleThenStep";

function isRuleGroup(item: unknown): item is RuleGroupType {
  return Boolean(item && typeof item === "object" && "rules" in item);
}

/** Normalize leaf values to sorted codes so name-only differences do not matter. */
function normalizeValueCodes(value: unknown): string[] {
  const parsed = parseEnhancedTaggingConditionValue(value);
  if (parsed.allValues) return ["*"];
  return parsed.conceptValues
    .map((item) => item.code.trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

function normalizeConditionForCompare(condition: unknown): unknown {
  if (!isRuleGroup(condition)) return null;
  return {
    combinator: String(condition.combinator ?? "and").toLowerCase(),
    not: Boolean(condition.not),
    rules: (condition.rules ?? []).map((item) => {
      if (isRuleGroup(item)) return normalizeConditionForCompare(item);
      const rule = item as RuleType;
      return {
        field: String(rule.field ?? "").trim(),
        operator: String(rule.operator ?? "").trim(),
        value: normalizeValueCodes(rule.value),
      };
    }),
  };
}

export function conditionsAreEquivalent(
  left: unknown,
  right: unknown,
): boolean {
  try {
    return (
      JSON.stringify(normalizeConditionForCompare(left)) ===
      JSON.stringify(normalizeConditionForCompare(right))
    );
  } catch {
    return false;
  }
}

export type DuplicateConditionalPlanMatch = {
  id: string;
};

type ConditionalPlanApiErrorItem = {
  field?: unknown;
  message?: unknown;
  details?: unknown;
};

function readExistingPlanIdFromDetails(details: unknown): string {
  if (!details || typeof details !== "object") return "";
  const record = details as Record<string, unknown>;
  return String(
    record.existing_plan_id ??
      record.existingPlanId ??
      record.plan_id ??
      record.planId ??
      record.id ??
      "",
  ).trim();
}

function extractExistingPlanIdFromErrorsArray(
  errors: unknown,
): string | null {
  if (!Array.isArray(errors)) return null;

  for (const item of errors) {
    if (!item || typeof item !== "object") continue;
    const errorItem = item as ConditionalPlanApiErrorItem;
    const existingId = readExistingPlanIdFromDetails(errorItem.details);
    if (!existingId) continue;
    const message = String(errorItem.message ?? "");
    const field = String(errorItem.field ?? "");
    // Duplicate conflict: condition field and/or "already exists" message.
    if (
      field === "condition" ||
      /already exists|duplicate|same condition/i.test(message)
    ) {
      return existingId;
    }
  }

  for (const item of errors) {
    if (!item || typeof item !== "object") continue;
    const existingId = readExistingPlanIdFromDetails(
      (item as ConditionalPlanApiErrorItem).details,
    );
    if (existingId) return existingId;
  }

  return null;
}

/**
 * Prefer structured API payload. Real backend shape nests under `data`:
 * `{ status, code, message, data: { errors: [{ details: { existing_plan_id } }] } }`
 * Also accepts a flat `{ errors: [...] }` body.
 */
export function extractExistingPlanIdFromApiPayload(
  payload: unknown,
): string | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;

  const fromTopLevel = extractExistingPlanIdFromErrorsArray(record.errors);
  if (fromTopLevel) return fromTopLevel;

  const data = record.data;
  if (data && typeof data === "object") {
    return extractExistingPlanIdFromErrorsArray(
      (data as Record<string, unknown>).errors,
    );
  }

  return null;
}

/**
 * Parse create/update API errors that indicate a duplicate conditional plan.
 * Prefer `errors[].details.existing_plan_id` from the API body.
 */
export function parseDuplicateConditionalPlanError(
  error: unknown,
): DuplicateConditionalPlanMatch | null {
  const responseData =
    error && typeof error === "object" && "responseData" in error
      ? (error as { responseData?: unknown }).responseData
      : null;

  const fromPayload = extractExistingPlanIdFromApiPayload(responseData);
  if (fromPayload) return { id: fromPayload };

  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  const trimmed = message.trim();
  if (!trimmed) return null;
  if (
    !/duplicate|already exists|same conditions|same condition|output dimension already exists/i.test(
      trimmed,
    )
  ) {
    return null;
  }

  const ruleMatch =
    trimmed.match(/\(\s*Rule\s*#?\s*([A-Za-z0-9_-]+)\s*\)/i) ??
    trimmed.match(/\bRule\s*#\s*([A-Za-z0-9_-]+)\b/i) ??
    trimmed.match(/\bRule\s+([A-Z]{1,10}\d+)\b/) ??
    trimmed.match(/\b(ENH\d+)\b/i);
  const id = ruleMatch?.[1]?.trim();
  return { id: id || "existing" };
}

/**
 * Find an existing conditional plan with the same object scope, tag, and
 * conditions. Excludes `excludeRuleId` (edit mode self-match).
 */
export async function findDuplicateConditionalPlan(options: {
  objectType: string;
  objectSubType: string;
  conditionQuery: RuleGroupType;
  thenTag: EnhancedTaggingThenTag;
  excludeRuleId?: string;
}): Promise<DuplicateConditionalPlanMatch | null> {
  const targetType = normalizePlanTypeApiCode(options.objectType);
  const concept = options.thenTag.dimensionCode.trim();
  const valueCode = options.thenTag.valueCode.trim();
  if (!targetType || !concept || !valueCode) return null;

  const query = buildConditionalPlansQuery({
    objectType: targetType,
    objectSubType: options.objectSubType,
    concept,
    conceptValueCode: valueCode,
    page: 1,
    pageSize: 100,
  });

  const { items } = await fetchConditionalPlans(query);
  const excludeId = options.excludeRuleId?.trim() ?? "";

  const match = items.find((plan: ConditionalPlanRecord) => {
    if (excludeId && plan.id === excludeId) return false;
    if (normalizePlanTypeApiCode(plan.target_type) !== targetType) return false;
    if (
      normalizePlanTypeApiCode(plan.sub_type) !==
      normalizePlanTypeApiCode(options.objectSubType)
    ) {
      return false;
    }
    if (plan.concept.trim() !== concept) return false;
    if (plan.concept_value.code.trim() !== valueCode) return false;
    return conditionsAreEquivalent(plan.condition, options.conditionQuery);
  });

  return match ? { id: match.id } : null;
}
